import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Star } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { reviewsAPI } from '../../services/api';

const MAX_LENGTH = 100;

/**
 * WriteReviewModal — submit a rating + short review for a target
 * (individual/academy/tournament). On success, calls onSubmitted(review)
 * so the caller can refresh its own list/summary without a full refetch.
 *
 * Props:
 *   isOpen        boolean
 *   onClose       () => void
 *   targetType    'individual' | 'academy' | 'tournament'
 *   targetId      string | number
 *   targetName    string — shown in the modal title
 *   onSubmitted   (review) => void
 */
const WriteReviewModal = ({
  isOpen, onClose, targetType, targetId, targetName, onSubmitted,
  // What this person said last time, if they have reviewed before.
  existingReview = null,
}) => {
  const { theme } = useTheme();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!existingReview;

  /**
   * OPEN HOLDING WHAT THEY WROTE BEFORE.
   *
   * A person may review a target once — the database enforces it and the server
   * upserts on it. The form did not know that, so somebody who had already
   * reviewed was shown the same empty stars and empty box as a first-time
   * reviewer, and submitting silently REPLACED their previous review. An edit
   * was happening through a control that said it was creating, with the old
   * words gone and nothing warning them.
   */
  /* KEYED ON THE REVIEW'S ID, NOT THE OBJECT.
   *
   * Depending on `existingReview` itself re-ran this on every refetch, because
   * each response is a fresh object — so the stars and the text reset underneath
   * anyone part-way through editing them. The id changes only when it is a
   * different review, which is the only time the form should be refilled.
   */
  const existingId = existingReview?.id ?? null;
  useEffect(() => {
    if (!isOpen) return;
    setRating(existingReview?.rating ?? 0);
    setReviewText(existingReview?.reviewText ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingId]);

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setReviewText('');
    onClose();
  };

  /**
   * ESCAPE CLOSES IT.
   *
   * Nothing listened for a key, so the only way out was a close button whose
   * icon was invisible — between the two, the dialog could not be dismissed at
   * all. Bound while open only, and removed on unmount, so a closed dialog is
   * not still swallowing keys from the page behind it.
   *
   * ABOVE THE EARLY RETURN, and it has to stay there. Put below it, this hook
   * only runs when the dialog is open, so React counts a different number of
   * hooks between renders and the whole page dies with "Rendered more hooks
   * than during the previous render". The `isOpen` guard inside is what makes
   * it a no-op while closed.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Please select a rating');
      return;
    }
    if (!reviewText.trim()) {
      toast.error('Please write a short review');
      return;
    }
    setSubmitting(true);
    try {
      const review = await reviewsAPI.create({ targetType, targetId, rating, reviewText: reviewText.trim() });
      toast.success('Review submitted — thank you!');
      onSubmitted?.(review);
      handleClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to submit review');
    }
    setSubmitting(false);
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      // Clicking the dimmed area behind the dialog closes it, which is what
      // everyone tries after Escape.
      onClick={handleClose}
    >
      {/* Stops a click inside the dialog from reaching the backdrop above. */}
      <div
        className={`${theme.card} rounded-lg max-w-md w-full overflow-hidden flex flex-col my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-4 border-b ${theme.border}`}>
          <h3 className={`text-lg font-semibold ${theme.text}`}>
            {isEditing
              ? (targetName ? `Your review of ${targetName}` : 'Your review')
              : (targetName ? `Review ${targetName}` : 'Write a Review')}
          </h3>
          {/* The icon had no colour of its own, so it inherited one that matched
              the card it sits on — the button was there the whole time and
              simply could not be seen. */}
          <button
            onClick={handleClose}
            aria-label="Close"
            className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-1" onMouseLeave={() => setHoverRating(0)}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  className="p-0.5"
                >
                  <Star
                    size={32}
                    className={n <= displayRating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                  />
                </button>
              ))}
            </div>
            {/* Fixed height reserved regardless of whether a label is showing — otherwise
                the text appearing/disappearing on hover reflows the modal and flickers. */}
            <span className={`text-sm font-medium h-5 ${theme.textSecondary} ${displayRating > 0 ? '' : 'invisible'}`}>
              {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][displayRating || 1]}
            </span>
          </div>

          <div>
            <textarea
              value={reviewText}
              onChange={e => setReviewText(e.target.value.slice(0, MAX_LENGTH))}
              placeholder="Share your experience..."
              rows={3}
              maxLength={MAX_LENGTH}
              className={`w-full px-3 py-2 border rounded-lg text-sm resize-none ${theme.input} ${theme.border} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            <div className={`text-right text-xs mt-1 ${theme.textMuted}`}>
              {reviewText.length}/{MAX_LENGTH}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-60 font-medium"
          >
            {submitting
              ? (isEditing ? 'Updating...' : 'Submitting...')
              : (isEditing ? 'Update Review' : 'Submit Review')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WriteReviewModal;
